import { Clock, CheckCircle, XCircle, Undo2, RotateCcw, Send, Rocket, ArchiveRestore } from 'lucide-react';

/**
 * Shared constants + derivation helpers for the review/attestation history
 * surfaces (FECOA-1611): the at-a-glance modal (`ReviewHistory`) and the full
 * dedicated audit page (`ReviewHistoryPage`). Both read the same
 * ReviewSubmission records; keeping the meta maps and cycle-prep logic here
 * means the two views can never drift on labels, colors, or version semantics.
 */

// The submission half of a cycle — always present.
export const SUBMIT_META = {
  label: 'Submitted for review',
  icon: Send,
  cls: 'badge-info',
};

// The decision half of a cycle, keyed by the submission's terminal status.
export const DECISION_META = {
  approved: { label: 'Approved', icon: CheckCircle, cls: 'badge-teal' },
  denied: { label: 'Denied', icon: XCircle, cls: 'badge-danger' },
  withdrawn: { label: 'Withdrawn', icon: Undo2, cls: 'badge-gray' },
  revoked: { label: 'Approval revoked', icon: RotateCcw, cls: 'badge-warning' },
  published: { label: 'Published', icon: Rocket, cls: 'badge-success' },
  unpublished: { label: 'Unpublished', icon: ArchiveRestore, cls: 'badge-warning' },
};

// Statuses that are a single standalone action rather than the decision half of
// a submit->decide cycle. These records are created already-decided by one
// actor (FECOA-1674 revoke, FECOA-1694 publish), so they render as one event
// with no phantom "Submitted for review" row preceding them.
export const STANDALONE_STATUSES = new Set(['published', 'revoked', 'unpublished']);

// Placeholder decision node shown while a submission is still pending, so the
// cycle always has two nodes and doesn't look truncated.
export const AWAITING_META = {
  label: 'Awaiting decision',
  icon: Clock,
  cls: 'bg-white text-gray-400 ring-gray-300 dark:bg-gray-800 dark:text-gray-500 dark:ring-white/15',
};

// Tone classes for `badge` chips (FECOA-2174), same status mapping as the cards.
// Header chip mirroring the entity's card badge, so the surface's story matches
// what the user clicked from. Keyed by the entity's current status.
export const CURRENT_STATUS_META = {
  draft: { label: 'Draft', cls: 'badge-warning' },
  in_review: { label: 'Pending Review', cls: 'badge-info' },
  pending: { label: 'Pending Review', cls: 'badge-info' },
  approved: { label: 'Approved', cls: 'badge-teal' },
  published: { label: 'Published', cls: 'badge-success' },
  denied: { label: 'Needs Revision', cls: 'badge-danger' },
  archived: { label: 'Archived', cls: 'badge-gray' },
};

// Plain-English explanation of the entity_version hash, shown on hover.
export const VERSION_TOOLTIP =
  'Unique system identifier for the exact content that was reviewed. '
  + 'If you ever need to trace this approval, or flag it as made in error, '
  + 'share this ID with support and they can pull up the full record.';

export const fmt = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export const ts = (value) => {
  const t = value ? new Date(value).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

// A withdrawal is the submitter recalling their own submission, not an approver
// decision — fall back to the submitter for the decider fields, and prefer the
// frozen signature name so the record stays consistent even if the approver
// later renames.
export const deciderOf = (r) => ({
  name: r.signed_by_name
    || r.decided_by_name
    || (r.status === 'withdrawn' ? r.submitted_by_name : null)
    || 'Unknown',
  email: r.status === 'withdrawn' ? r.submitted_by_email : r.signed_by_email,
});

// Sort oldest-first and attach version-change info to each cycle. We only
// surface the version when it changes: the hash on the first submission and on
// any resubmission whose content differs from the prior one; identical
// resubmissions collapse to a muted "unchanged" note. A repeated hash is noise
// — what a reviewer wants is "did anything change since I last saw this?".
// Returns `{ r, ci, decided, decisionMeta, version }` per cycle, where
// `version.kind` is one of 'none' | 'reviewed' | 'changed' | 'unchanged'.
// The returned order is chronological (version diffing depends on it);
// display surfaces reverse it so the newest activity renders on top (FECOA-1599).
export const prepareCycles = (rows) => {
  const cycles = [...(rows || [])].sort((a, b) => ts(a.submitted_at) - ts(b.submitted_at));
  return cycles.map((r, ci) => {
    const prevVersion = ci > 0 ? cycles[ci - 1].entity_version : null;
    const versionChanged = ci === 0 || r.entity_version !== prevVersion;
    let version;
    if (!r.entity_version) {
      version = { kind: 'none' };
    } else if (versionChanged) {
      version = { kind: ci === 0 ? 'reviewed' : 'changed', hash: String(r.entity_version).slice(0, 8) };
    } else {
      version = { kind: 'unchanged' };
    }
    const decided = r.is_decided;
    const decisionMeta = decided ? (DECISION_META[r.status] || DECISION_META.approved) : AWAITING_META;
    const standalone = STANDALONE_STATUSES.has(r.status);
    return { r, ci, decided, decisionMeta, version, standalone };
  });
};
